import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  MessageSquare,
  ShoppingCart,
  Users,
  Mail,
  Upload,
  Settings,
  HelpCircle,
  LogOut,
  ChevronUp,
  Building2,
  BarChart3,
  Info,
  Briefcase,
  UserCircle,
} from "lucide-react"

import { useAuth } from "../context/AuthContext"
import API_CONFIG from '../config/api'
import profileImage from '../assets/profile.svg'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "./ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import ProfileDropdown from "./common/ProfileDropdown"
import { debug } from '../utils/debugUtils'

// Menu items data
const data = {
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Analytics & Reporting",
      url: "/analytics",
      icon: BarChart3,
    },
    {
      title: "Quotes",
      url: "/quotes",
      icon: MessageSquare,
    },
    {
      title: "Orders",
      url: "/orders",
      icon: ShoppingCart,
    },
    {
      title: "Customers",
      url: "/customers",
      icon: Users,
    },
    {
      title: "Emails",
      url: "/email_display",
      icon: Mail,
    },
    {
      title: "Onboarding",
      url: "/onboarding",
      icon: Upload,
    },
  ],
  navInfo: [
    {
      title: "About Us",
      url: "/about",
      icon: Info,
    },
    {
      title: "Services",
      url: "/services",
      icon: Building2,
    },
    {
      title: "Our Team",
      url: "/team",
      icon: UserCircle,
    },
    {
      title: "Careers",
      url: "/careers",
      icon: Briefcase,
    },
  ],
  navSecondary: [
    {
      title: "Production Settings",
      url: "/settings",
      icon: Settings,
    },
    {
      title: "Help Center",
      url: "/help",
      icon: HelpCircle,
    },
  ],
}

// Navigation Component
function NavMain({ items }) {
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = (url) => {
    return location.pathname === url
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Main Navigation</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton 
              onClick={() => navigate(item.url)}
              isActive={isActive(item.url)}
              tooltip={item.title}
              className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:h-full"
            >
              {item.icon && <item.icon className="group-data-[collapsible=icon]:!m-0 group-data-[collapsible=icon]:block group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:my-auto" />}
              <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

// Info Navigation Component
function NavInfo({ items }) {
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = (url) => {
    return location.pathname === url
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Information</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton 
              onClick={() => navigate(item.url)}
              isActive={isActive(item.url)}
              tooltip={item.title}
              className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:h-full"
            >
              {item.icon && <item.icon className="group-data-[collapsible=icon]:!m-0 group-data-[collapsible=icon]:block group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:my-auto" />}
              <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

// Secondary Navigation Component
function NavSecondary({ items }) {
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = (url) => {
    return location.pathname === url
  }

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton 
              onClick={() => navigate(item.url)}
              isActive={isActive(item.url)}
              tooltip={item.title}
              className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:h-full"
            >
              {item.icon && <item.icon className="group-data-[collapsible=icon]:!m-0 group-data-[collapsible=icon]:block group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:my-auto" />}
              <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

// Company Logo/Branding Component
function CompanyLogo() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton 
          size="lg" 
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center w-full flex justify-center"
        >
          {!isCollapsed && (
            <img
              src="/assets/company name 2.png"
              alt="Company Name"
              style={{ height: 64, display: 'block', margin: '0 auto' }}
            />
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar({ ...props }) {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [userEmail, setUserEmail] = useState('Welcome')

  // Get user email - same logic as Nav.js
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        // Method 1: Try getting from /auth/me first
        try {
          const meResponse = await fetch(`${API_CONFIG.baseURL}/auth/me`, {
            method: 'GET',
            credentials: 'include'
          });

          if (meResponse.ok) {
            const userData = await meResponse.json();
            if (userData && userData.email) {
              setUserEmail(userData.email);
              return;
            }
          }
        } catch (error) {
          debug.component.sidebar('Could not get email from /auth/me', error.message);
        }

        // Method 2: Fallback to quotes data
        try {
          const quotesResponse = await fetch(`${API_CONFIG.baseURL}/quotes`, {
            method: 'GET',
            credentials: 'include'
          });

          if (quotesResponse.ok) {
            const quotesData = await quotesResponse.json();
            if (quotesData && quotesData.length > 0) {
              const emailFromQuotes = quotesData[0].userEmail || quotesData[0].customer?.email || quotesData[0].email;
              if (emailFromQuotes) {
                setUserEmail(emailFromQuotes);
                return;
              }
            }
          }
        } catch (error) {
          debug.component.sidebar('Could not get email from quotes', error.message);
        }

        // Method 3: Fallback to customers data
        try {
          const customersResponse = await fetch(`${API_CONFIG.baseURL}/customers`, {
            method: 'GET',
            credentials: 'include'
          });

          if (customersResponse.ok) {
            const customersData = await customersResponse.json();
            if (customersData && customersData.length > 0) {
              const emailFromCustomers = customersData[0].userEmail || customersData[0].email;
              if (emailFromCustomers) {
                setUserEmail(emailFromCustomers);
                return;
              }
            }
          }
        } catch (error) {
          debug.component.sidebar('Could not get email from customers', error.message);
        }

        // If all methods fail
        setUserEmail('Welcome');
      } catch (error) {
        debug.error('Sidebar: Authentication error', error);
        setUserEmail('Please login');
      }
    };

    fetchUserEmail();
  }, []);

  const handleLogout = async () => {
    try {
      await logout()
      navigate("/")
    } catch (error) {
      debug.error("Logout error", error)
    }
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <CompanyLogo />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavSecondary items={data.navSecondary} />
      </SidebarContent>
      <SidebarFooter>
        <ProfileDropdown user={{ email: userEmail }} onLogout={handleLogout} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
} 